import { useEffect, useRef, useState } from 'react';

type UseDebouncedValueOutput<T> = {
  debouncedValue: T;
  isPending: boolean;
};

type UseDebouncedValueParams<T> = {
  value: T;
  delay: number;
  shouldSkipDelay?: (oldValue: T, newValue: T) => boolean;
};

const alwaysFalse = () => false;

export const useDebouncedValue = <T>({
  value,
  delay,
  shouldSkipDelay = alwaysFalse,
}: UseDebouncedValueParams<T>): UseDebouncedValueOutput<T> => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [isPending, setIsPending] = useState(false);
  const isFirstRender = useRef(true);
  const settledValueRef = useRef(value);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }

    if (shouldSkipDelay(settledValueRef.current, value)) {
      setDebouncedValue(value);
      setIsPending(false);
      settledValueRef.current = value;
      return undefined;
    }

    setIsPending(true);
    const timer = setTimeout(() => {
      setDebouncedValue(value);
      setIsPending(false);
      settledValueRef.current = value;
    }, delay);
    return () => clearTimeout(timer);
  }, [value, delay, shouldSkipDelay]);

  return { debouncedValue, isPending };
};
